import React from 'react';

import { ArrowUpDownIcon } from 'lucide-react';
import { PaginationTable } from 'src/domains/casino/webview/bonus-engine/common/components/pagination-table/PaginationTable';
import { EligibleUsersState } from 'src/domains/casino/webview/bonus-engine/campaign/player-management/eligible-users/EligibleUsers.state';
import { EligibleUsersTableState } from 'src/domains/casino/webview/bonus-engine/campaign/player-management/eligible-users/EligibleUsersTable.state';
import { Button } from 'src/domains/common/components/button/button';
import { IconButton } from 'src/domains/common/components/button/icon-button';
import { Input } from 'src/domains/common/components/input/input';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from 'src/domains/common/components/table/table';
import { cn } from 'src/domains/common/components/utils';
import { observer } from 'src/utils/mobx-react';

export const EligibleUsersTable = observer(
    'ElibleUsersTable',
    ({
        state,
        form,
        disabled,
        onDelete,
        onDeleteAll,
    }: {
        state: EligibleUsersTableState;
        form: EligibleUsersState['playerIdsCsvForm'];
        disabled: boolean;
        onDelete: (id: number) => void;
        onDeleteAll: () => void;
    }) => {
        const { paginatedData, totalItemsView, setSortConfig, filter, setFilter, paginationConfig } = state;
        const errors = form.errorsForView;
        const isFormValid = errors.length === 0;
        const paginatedDataToShow = paginatedData.length > 0;
        const tableBorderClas = isFormValid ? 'border-border' : 'border-destructive';

        return (
            <div className='flex flex-col flex-nowrap min-h-[350px] gap-2 mt-5'>
                <div className='m-0 text-[1rem] font-semibold leading-[1.75]'>Total added: {totalItemsView}</div>
                <div className='flex items-center justify-between py-2'>
                    <div className='flex gap-4'>
                        <Input
                            placeholder='Search'
                            formInputState={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className='max-w-sm py-2'
                            errors={[]}
                            dataTest='search-input'
                        />
                    </div>
                    <div className='flex gap-4'>
                        <Button
                            className='min-w-36'
                            variant='destructive'
                            onClick={onDeleteAll}
                            disabled={disabled || paginatedDataToShow === false}
                        >
                            Clear All
                        </Button>
                    </div>
                </div>

                <div className={cn(tableBorderClas, 'relative w-full overflow-auto border border-solid rounded-md')}>
                    <Table className='table-fixed'>
                        <TableHeader className='sticky top-0 bg-white z-[1] shadow-b-shadow'>
                            <TableRow className='!border-b-0'>
                                <TableHead className='py-0 h-14 w-[100%]'>
                                    <div
                                        className='cursor-pointer flex items-center gap-2.5'
                                        onClick={setSortConfig}
                                    >
                                        Player ID
                                        <ArrowUpDownIcon className='size-4' />
                                    </div>
                                </TableHead>
                                <TableHead className='py-0 !px-2 h-14 w-[100px] text-center'>Delete</TableHead>
                            </TableRow>
                        </TableHeader>

                        <TableBody>
                            {paginatedDataToShow ? (
                                paginatedData.map((playerId, index) => {
                                    const rowClass = index % 2 === 0 ? 'bg-white' : 'bg-muted/50';

                                    return (
                                        <React.Fragment key={playerId}>
                                            <TableRow
                                                className={cn(
                                                    rowClass,
                                                    'data-[state=selected]:bg-muted border-border border-solid border-t-0 border-l-0 border-r-0'
                                                )}
                                                data-test={`playerId-row-${playerId}`}
                                            >
                                                <TableCell
                                                    className='h-14'
                                                    data-test='playerId-name'
                                                >
                                                    {playerId}
                                                </TableCell>
                                                <TableCell className='h-14'>
                                                    <div className='flex gap-4 justify-center'>
                                                        <IconButton
                                                            onClick={() => onDelete(playerId)}
                                                            className='text-destructive hover:text-destructive cursor-pointer'
                                                            icon='trash'
                                                            size='sm'
                                                            title='Delete'
                                                            variant='ghost'
                                                            disabled={disabled}
                                                            data-test='playerId-delete-button'
                                                        />
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={7}
                                        className='h-24 text-center'
                                        data-test='no-results'
                                    >
                                        No Player IDs were added.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
                {isFormValid === false && <span className='text-destructive text-sm'>{errors}</span>}
                <PaginationTable config={paginationConfig} />
            </div>
        );
    }
);
